import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ManageAssetsStep} from '../Step/ManageAssetsStep';
import {
  AssetStatus,
  AssistantType,
  AutoFlowError,
  AutoFlowErrorCode,
  ErrorCode,
  NumBool,
  SERVER_ERROR_CODE,
} from '@taopix/taopix-core';
import {CoverMode, CreateCoverStep} from '../Step/CreateCoverStep';
import {SmartSelectionStep} from './Step/SmartSelectionStep';
import {PreviewStep} from '../Step/PreviewStep';
import {UserFlowStep} from '../../../Facade/UserFlow/BaseUserFlow';
import {
  AssetMetaInterface,
  ReducerState,
  SmartDesignArgs,
  SmartDesignResponse,
} from '@taopix/taopix-core/dist/types/types';
import {MediaGalleryProps} from '../../MediaGallery/MediaGallery';
import {SubStep} from '../Step/SubStep/SubStep';
import {WelcomeStep} from '../Step/WelcomeStep';
import {AssistantActions} from '../../Context/Assistant/AssistantActionTypes';
import {useTaopix} from '../../Context/TaopixContext';
import {AssetWorkflow, uploadStats} from '@taopix/taopix-asset-manager';
import WaitingDialog from '../../Dialogs/SmartDesign/WaitingDialog/WaitingDialog';
import {LoadingOverlay} from '@taopix/taopix-design-system';
import {useUserFlowState} from '../../Context/UserFlow/UserFlowContext';
import {useErrorBoundary} from 'react-error-boundary';
import {UserFlowType} from '../../../Facade/UserFlow/UserFlowFacade';
import {useAssistant} from '../../Context/Assistant/AssistantContext';
import useAIStatusChecker from '../../../Hooks/Assets/useAIStatusChecker';
import {AssistantCommonProps, AssistantHeaderProps, AssistantNavigationProps} from '../Assistant';
import {UserFeedbackActions} from '../../Context/UserFeedback/UserFeedbackActionTypes';
import {useUserFeedback} from '../../Context/UserFeedback/UserFeedbackContext';
import {useAssetStats} from '../../Context/Assets/AssetStatsContext';
import {useExperience} from '../../Experience/ExperienceProvider';

type SmartDesignAssistantProps = {
  assets: AssetMetaInterface[];
  mediaGalleryProps: MediaGalleryProps;
  onAfterRun: () => void;
} & AssistantCommonProps &
  AssistantNavigationProps &
  AssistantHeaderProps;

enum SmartDesignState {
  None,
  Started,
  CallInProgress,
}

type Response = ReducerState & SmartDesignResponse & {fromCache?: boolean};

export const SmartDesignAssistant = ({assets, backButton, ...props}: SmartDesignAssistantProps) => {
  const taopix = useTaopix();
  const assistant = useAssistant();
  const userFlowState = useUserFlowState();
  const userFeedback = useUserFeedback();
  const monitor = useAIStatusChecker();
  const {experience} = useExperience();
  const {
    state: {aiPercentage},
  } = useAssetStats();
  const {showBoundary} = useErrorBoundary();
  const {t} = useTranslation(['assistants']);

  const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
  const [waitingDialogOpen, setWaitingDialogOpen] = useState<boolean>(false);
  const [loadingOpen, setLoadingOpen] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const processTimeout = useRef(0);
  const smartDesignState = useRef<SmartDesignState>(SmartDesignState.None);
  const smartDesignCallback = useRef({
    onComplete: (response: any) => {},
    onError: (error: any) => {},
  });
  const stateManager = taopix.core.get('stateManager');
  const isDryRun =
    !assistant.state.useAllPictures &&
    experience.WIZARD.smartPictureSelectionStep.showSmartPictureSelectionStep;

  useEffect(() => {
    if (!taopix.state.loaded) return;
    if (100 === aiPercentage) {
      if (SmartDesignState.Started === smartDesignState.current) {
        // Call the Smart Design again to get the result.
        smartDesignState.current = SmartDesignState.None;

        processSmartDesign(
          false,
          isDryRun,
          false,
          smartDesignCallback.current.onComplete,
          smartDesignCallback.current.onError
        );
      }
    }

    // Set the progress value to the current value (but not lower than 5 to ensure we display some progress immediately).
    setUploadProgress(Math.min(80, Math.max(5, aiPercentage)));
  }, [taopix.state.loaded, assistant.state.useAllPictures, aiPercentage]);

  const onAssetUpdate = useCallback(
    event => {
      event.detail.forEach(eventAsset => {
        const asset = stateManager.getAsset(eventAsset.id as number);
        if (!asset) {
          return;
        }

        const newAsset = {
          ...asset,
          metaData: {
            ...asset.metaData,
            status: eventAsset.metaData.status,
            regions: eventAsset.metaData.regions.split(/\r/).filter(String),
            dateTimeOriginal: eventAsset.metaData.dateTimeOriginal,
          },
        };

        return stateManager.updateAsset(newAsset);
      });
    },
    [taopix.state.loaded]
  );

  useEffect(() => {
    monitor?.on('update', onAssetUpdate);

    return () => {
      monitor?.off('update', onAssetUpdate);
    };
  }, [monitor, onAssetUpdate]);

  const runSmartDesign = (
    save: boolean,
    dryRun: boolean,
    checkJobStatus: boolean,
    resolve: (response: Response) => void,
    reject: (response: any) => void,
    coverItems?: any
  ) => {
    smartDesignState.current = SmartDesignState.CallInProgress;

    run(save, dryRun, checkJobStatus, coverItems)
      .then(response => {
        // Call completed, mark that another call can go off.
        smartDesignState.current = SmartDesignState.Started;

        if (ErrorCode.None !== response.errorCode) {
          reject(response.error);
          return;
        }

        if (
          response.assetsToCheckCount +
            response.assetsQueued.length +
            response.assetsNotUploadedCount >
          0
        ) {
          if (monitor) {
            // If there are some assets which need to be checked then set up the monitor to track them.
            if (response.assetsToCheckCount + response.assetsQueued.length > 0) {
              if (response.assetsQueued.length > 0) {
                response.assetsQueued.forEach(id => {
                  const asset = stateManager.getAsset(id);
                  if (asset && !Object.prototype.hasOwnProperty.call(asset, 'uploadId')) {
                    uploadStats.up(AssetWorkflow.AI, 'TOTAL');
                    uploadStats.up(AssetWorkflow.AI, 'READY');
                  }
                  monitor.add(id);
                });
                smartDesignState.current = SmartDesignState.Started;
              }
            } else {
              // If there are some assets which we are waiting to upload check back in 5 seconds to see if they have been uploaded.
              window.clearTimeout(processTimeout.current);
              processTimeout.current = window.setTimeout(() => {
                runSmartDesign(false, dryRun, false, resolve, reject, response.coverItems);
              }, 5000);
            }
          }
        } else if (NumBool.False === response.aiJobStatus) {
          // If the AI job is not ready retry in 2 seconds.
          window.clearTimeout(processTimeout.current);
          setTimeout(async () => {
            runSmartDesign(false, dryRun, true, resolve, reject, response.coverItems);
          }, 2000);
        } else if (response.assetsUsed.length > 0) {
          monitor?.off('update', onAssetUpdate);
          // Finished, call the resolve from the calling function.
          resolve(response);
        }
      })
      .catch(error => {
        smartDesignState.current = SmartDesignState.None;
        reject(error);
      });
  };

  const processSmartDesign = async (
    save: boolean,
    dryRun: boolean,
    checkJobStatus: boolean,
    resolve: (response: Response) => void,
    reject: (response: any) => void
  ) => {
    window.clearTimeout(processTimeout.current);

    if (smartDesignState.current === SmartDesignState.CallInProgress) {
      return;
    }

    runSmartDesign(save, dryRun, checkJobStatus, resolve, reject);
  };

  const startSmartDesign = (dryRun: boolean): Promise<void | Response> => {
    const hasSmartSelection =
      dryRun &&
      !assistant.state.useAllPictures &&
      !assistant.state.changeDetected &&
      assistant.state.selectedAssets.length > 0;

    // Check if any assets are not processed for AI.
    const showWaitingDialog = assets.some(asset => {
      return AssetStatus.Processed !== asset.metaData.status;
    });

    if (showWaitingDialog) {
      // Show the waiting dialog while assets are processed. Start the progress at 5 to ensure we show some progress immediately.
      setUploadProgress(5);
      setWaitingDialogOpen(true);
    } else {
      if (!hasSmartSelection) {
        // If all assets are processed for AI, then show the loading overlay.
        setLoadingOpen(true);
      }
    }

    // The Smart Design runs multiple times so we need to wait for all of them to finish.
    return new Promise<void | Response>((resolve, reject) => {
      smartDesignCallback.current = {onComplete: resolve, onError: reject};

      if (hasSmartSelection) {
        resolve({
          assetsUsed: assistant.state.selectedAssets,
          fromCache: true,
        } as unknown as Response);
      } else {
        runSmartDesign(true, dryRun, false, resolve, reject);
        assistant.dispatch(AssistantActions.setChangeDetected(false));
      }
    })
      .then(response => {
        if (!response) return;
        if (response.historyAction) {
          assistant.dispatch(AssistantActions.setHistoryID(response.historyAction));
        }
        return response;
      })
      .catch(error => {
        userFeedback.dispatch(
          UserFeedbackActions.setErrorUserFeedback({
            title: '',
            message: error.message,
            showHeader: !(error instanceof AutoFlowError),
          })
        );
        setLoadingOpen(false);
      })
      .finally(() => {
        smartDesignState.current = SmartDesignState.None;
      });
  };

  const run = (
    save: boolean,
    dryRun: boolean,
    checkAIJobStatus: boolean,
    coverItems?: any
  ): Promise<Response> => {
    const useAllPictures = (() => {
      // If Smart Picture Selection is off, don't use all pictures
      // so we get the final result.
      if (
        !assistant.state.useAllPictures &&
        !experience.WIZARD.smartPictureSelectionStep.showSmartPictureSelectionStep
      ) {
        return false;
      }

      return !dryRun;
    })();

    const args: SmartDesignArgs = {
      save: save,
      showLoading: false,
      createPreviewThumbnail: false,
      coverItems: coverItems ? coverItems : assistant.formatCoverItems(),
      useAllPictures: useAllPictures,
      selectedPictures:
        dryRun || assistant.state.useAllPictures ? [] : assistant.state.selectedAssets,
      checkImageUsage: true,
      checkAIJobStatus: checkAIJobStatus,
      dryRun: dryRun,
      pageCount: assistant.state.setPageCount ? assistant.state.pageCount : -1,
      pageNumber: -1,
    };

    return taopix.core
      .get('stateManager')
      .call<SmartDesignArgs, ReducerState & SmartDesignResponse>('smartdesign', args)
      .then(response => {
        return response;
      })
      .catch(error => {
        if (error instanceof AutoFlowError) {
          throw new AutoFlowError(AutoFlowErrorCode.Generic, error.message);
        } else if (SERVER_ERROR_CODE === error.response?.status) {
          // Display our error.
          throw new AutoFlowError(AutoFlowErrorCode.Generic, error.response.data.message);
        } else {
          // Display the Axios error message.
          throw new Error(error.message);
        }
      });
  };

  const subStepPanel = (
    <SubStep
      open={optionsOpen}
      onLastStep={() => {
        setOptionsOpen(false);
        smartDesignState.current = SmartDesignState.Started;

        // First pass.
        startSmartDesign(isDryRun)
          .then(response => {
            if (!response) return;

            props.onAfterRun();
            assistant.dispatch(AssistantActions.setSelectedAssets(response.assetsUsed, true));

            if (response.hasOwnProperty('fromCache')) {
              props.nextButton.props.onClick();
            } else {
              if (waitingDialogOpen) {
                setUploadProgress(100);
              }
              window.setTimeout(
                () => {
                  props.nextButton.props.onClick();
                  setWaitingDialogOpen(false);
                  setLoadingOpen(false);
                },
                waitingDialogOpen ? 1000 : 0
              );
            }
          })
          .catch(error => {
            assistant.dispatch(AssistantActions.setSelectedAssets([], false));
            setWaitingDialogOpen(false);
            setLoadingOpen(false);
            showBoundary(error);
          })
          .finally(() => {
            smartDesignState.current = SmartDesignState.None;
          });
      }}
      onClose={() => setOptionsOpen(false)}
    />
  );

  const getNextButton = (): JSX.Element => {
    return React.cloneElement(props.nextButton, {
      ...props.nextButton.props,
      onClick: () => {
        // Look ahead to the next step to see if we need to show the options substep.
        const currentStep = userFlowState.getCurrentStep(UserFlowType.Assistant);
        const nextStepResult: string = userFlowState.getNextStep(UserFlowType.Assistant);

        if (
          [
            `${UserFlowType.Assistant}.${UserFlowStep.AssistantManageAssets}`,
            `${UserFlowType.Assistant}.${UserFlowStep.AssistantFrontCover}`,
            `${UserFlowType.Assistant}.${UserFlowStep.AssistantBackCover}`,
          ].includes(currentStep)
        ) {
          if (
            [
              `${UserFlowType.Assistant}.${UserFlowStep.None}`,
              `${UserFlowType.Assistant}.${UserFlowStep.AssistantSmartSelection}`,
              `${UserFlowType.Assistant}.${UserFlowStep.AssistantPreview}`,
            ].includes(nextStepResult)
          ) {
            setOptionsOpen(true);
          } else {
            props.nextButton.props.onClick();
          }
        } else {
          props.nextButton.props.onClick();
        }
      },
    });
  };

  let currentStep = <></>;
  switch (true) {
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantWelcome): {
      currentStep = (
        <WelcomeStep
          {...props}
          nextButton={getNextButton()}
          onClose={props.onClose}
          assistantType={AssistantType.SmartDesign}
        />
      );
      break;
    }
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantManageAssets): {
      currentStep = (
        <ManageAssetsStep
          {...props}
          assets={assets}
          backButton={backButton}
          nextButton={getNextButton()}
        />
      );
      break;
    }
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantFrontCover): {
      currentStep = (
        <CreateCoverStep
          {...props}
          assets={assets}
          backButton={backButton}
          nextButton={getNextButton()}
          coverMode={CoverMode.Front}
          key={`cover-step-${CoverMode.Front}`}
          canShowTextDefaultMode={
            !optionsOpen && smartDesignState.current === SmartDesignState.None
          }
        />
      );
      break;
    }
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantBackCover): {
      currentStep = (
        <CreateCoverStep
          {...props}
          assets={assets}
          backButton={backButton}
          nextButton={getNextButton()}
          coverMode={CoverMode.Back}
          key={`cover-step-${CoverMode.Back}`}
          canShowTextDefaultMode={
            !optionsOpen && smartDesignState.current === SmartDesignState.None
          }
        />
      );
      break;
    }
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantSmartSelection): {
      currentStep = (
        <SmartSelectionStep
          assets={assets}
          startSmartDesign={startSmartDesign}
          setLoadingDialogOpen={setLoadingOpen}
          setWaitingDialogOpen={setWaitingDialogOpen}
          {...props}
          backButton={backButton}
        />
      );
      break;
    }
    case userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantPreview): {
      currentStep = <PreviewStep {...props} backButton={backButton} />;
      break;
    }
  }

  return (
    <>
      {currentStep}
      {subStepPanel}
      <WaitingDialog
        open={waitingDialogOpen}
        useAllPictures={assistant?.state?.useAllPictures}
        uploadProgress={uploadProgress}
      />
      <LoadingOverlay
        id={'smartassistant'}
        open={loadingOpen}
        label={
          assistant.state.useAllPictures ||
          userFlowState.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantSmartSelection)
            ? t('*:str_TitleCreatingProject')
            : t('*:str_TitleSelectingBestPictures')
        }
      />
    </>
  );
};
